import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";

const isCut = (el: HTMLElement | undefined) => !!el && el.scrollWidth > el.clientWidth;

/**
 * A switcher row's name plus a muted detail qualifying it. The wrapping line
 * settles their priority without measuring: the detail drops to a second line
 * rather than eating into the name, so the name is the only thing that ever
 * truncates and it truncates against the full row width.
 */
export const NavRowLabel: Component<{ name: string; detail?: string; current?: boolean }> = (props) => {
  const [truncated, setTruncated] = createSignal(false);
  let nameRef: HTMLElement | undefined;
  let detailRef: HTMLElement | undefined;

  onMount(() => {
    const measure = () => setTruncated(isCut(nameRef) || isCut(detailRef));
    // No eager measure: observe() delivers the first one.
    const observer = new ResizeObserver(measure);
    if (nameRef) observer.observe(nameRef);
    if (detailRef) observer.observe(detailRef);
    onCleanup(() => observer.disconnect());
  });

  return (
    <StTooltip
      placement="right"
      disabled={!truncated()}
      content={[props.name, props.detail].filter(Boolean).join(" · ")}
    >
      <StTooltipTrigger as="span" class="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
        <span ref={nameRef} class="min-w-0 truncate" classList={{ "font-semibold": props.current }}>
          {props.name}
        </span>
        <Show when={props.detail}>
          {(detail) => (
            <span ref={detailRef} class="min-w-0 truncate text-xs text-muted-text">
              {detail()}
            </span>
          )}
        </Show>
      </StTooltipTrigger>
    </StTooltip>
  );
};
